/**
 * ReinforceUI — placement phase. Tap your own territory (or, in a team game,
 * a teammate's) to add 1 army.
 * Player legend + PlacementHud sit ABOVE the map as inline flex children so
 * they never cover map territories. Per-territory pending armies show up
 * on the map badges via `armyOverrides`.
 */

import React, { useState, useMemo, useCallback } from 'react'
import { View, ScrollView } from 'react-native'
import { MapView } from '../map/MapView'
import { MapControlsBarSlot } from '../map/MapControlsBar'
import { PlacementHud, type PlacementStepMode } from '../map/PlacementHud'
import {
  useGameMapSceneRegistration,
  type GameMapSceneRegistration,
} from '../map/gameMapScene'
import { placementBonusText } from '../../lib/placementBonus'
import { useMapDimensions } from '../../lib/useMapDimensions'
import { useReinforce } from '../../hooks/useGames'
import { reinforcementCount } from '../../lib/gameState'
import {
  derivePendingCounts,
  totalStackArmies,
  stackToPlacementList,
  type PlacementAction,
} from '../../lib/placementStack'
import type { GameState } from '../../lib/gameState'
import { armyHeadroom, placeableArmies } from '@warcarp/api-types'
import { placeableTerritories } from '../../lib/teamPlay'
import type { GamePlayerResponse } from '@warcarp/api-types'
import type { PanOffset } from '../../lib/mapZoom'

interface ReinforceUIProps {
  gameId: string
  state: GameState
  myPlayerId: string
  slotByPlayerId: Record<string, number>
  /** For the on-map player legend. */
  gamePlayers: GamePlayerResponse[]
  viewerUserId?: string
  /** Forwarded to MapView so the player-roster strip centres on the
   * map, not the screen (HUD occupies bottom/side during placement). */
  mapRightOverlay?: React.ReactNode
  regionToggleNode?: React.ReactNode
  regionToggleCorner?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
  zoomToggleNode?: React.ReactNode
  zoomToggleCorner?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
  /**
   * Optional header rendered at the TOP of the landscape action panel
   * (above the HUD). The game screen colocates the cog + horizontal
   * roster strip here so neither floats over the map.
   */
  actionPanelHeader?: React.ReactNode
  /** Wide layout with persistent chat/log: size the panel to its content
   *  (scrolling past its cap) instead of filling the column. */
  fitContent?: boolean
  /**
   * Optional cards HUD chip — docked in the placement HUD's top-right
   * corner. Lives here (rather than at session-screen overlay level)
   * because turning in cards is only meaningful during placement, so
   * the chip naturally hides when the placement HUD doesn't render.
   */
  cardChip?: React.ReactNode
  /**
   * Optional thin control bar rendered directly BENEATH the map (zoom + view
   * mode). When provided it sits between the map and the HUD in portrait, and
   * beneath the map in the right pane in landscape.
   */
  mapControlsBar?: React.ReactNode
  /** Forwarded to MapView — draw region-bonus badges on the board. */
  showRegionBonuses?: boolean
  /** Forwarded to MapView — zoom-and-pan state managed by the game screen. */
  zoomEnabled?: boolean
  panOffset?: PanOffset
  onPanChange?: (offset: PanOffset) => void
  zoomScale?: number
  onZoomScaleChange?: (scale: number) => void
  /** Forwarded to MapView for bot-attack animation fix (see useAttackEvents). */
  viewerJoinedAt?: number
  /** Time-machine hook — scrubs the game screen into the snapshot view at the given event index. */
  onViewAtEvent?: (eventIndex: number) => void
  selectedEventIndex?: number | null
  /**
   * Optional CONTROLLED pending-placement state. Lifted to the parent so the
   * staged placements survive a trip to the regions overlay (which unmounts
   * this component). When omitted, the component manages its own local state.
   */
  placementStack?: PlacementAction[]
  onPlacementStackChange?: React.Dispatch<React.SetStateAction<PlacementAction[]>>
  stepMode?: PlacementStepMode
  onStepModeChange?: React.Dispatch<React.SetStateAction<PlacementStepMode>>
  /** When set, the parent owns the single shared MapView. */
  mapSceneKey?: string
  onMapSceneConfigChange?: GameMapSceneRegistration
}

export function ReinforceUI({
  gameId,
  state,
  myPlayerId,
  slotByPlayerId,
  gamePlayers,
  viewerUserId,
  mapRightOverlay,
  regionToggleNode,
  regionToggleCorner,
  zoomToggleNode,
  zoomToggleCorner,
  actionPanelHeader,
  fitContent = false,
  cardChip,
  mapControlsBar,
  showRegionBonuses,
  zoomEnabled,
  panOffset,
  onPanChange,
  zoomScale,
  onZoomScaleChange,
  viewerJoinedAt,
  placementStack: placementStackProp,
  onPlacementStackChange,
  stepMode: stepModeProp,
  onStepModeChange,
  mapSceneKey,
  onMapSceneConfigChange,
}: ReinforceUIProps) {
  const reinforce = useReinforce(gameId)
  const viewerSlot = slotByPlayerId[myPlayerId] ?? 1
  const rawBudget = useMemo(() => reinforcementCount(state, myPlayerId), [state, myPlayerId])
  // Troop caps can leave less room than the budget; the server requires the
  // placed total to match what actually FITS, so the Commit button has to agree
  // or the player is stuck with armies they can never place.
  // Placeable = own territories plus, in a team game, teammates' (the
  // server's canReinforce uses the same set for the cap headroom).
  const budget = placeableArmies(rawBudget, placeableTerritories(state, myPlayerId))
  // The "// includes ..." bonus subtitle — see placementBonusText. In the
  // reinforce phase any bonus armies come from a card turn-in.
  const bonusText = placementBonusText(state, myPlayerId, state.phase)

  // Own territories, plus a teammate's in a team game (canPlaceOn).
  const myTerritories = useMemo(
    () => placeableTerritories(state, myPlayerId),
    [state, myPlayerId],
  )
  const myTerritoryIds = useMemo(() => myTerritories.map((t) => t.id), [myTerritories])

  // Stack-of-actions model: each tap pushes { territoryId, armies }.
  // Undo pops the tail; Reset clears the whole stack.
  // Pending counts are derived by reducing the stack so the map re-renders
  // correctly after any mutation without needing a separate map state.
  // Controlled by the parent when provided (so placements survive the regions
  // overlay unmounting this component); otherwise local.
  const [localStack, setLocalStack] = useState<PlacementAction[]>([])
  const placementStack = placementStackProp ?? localStack
  const setPlacementStack = onPlacementStackChange ?? setLocalStack
  // Per-tap drop amount. Default 1 keeps the original "tap = +1"
  // behaviour for anyone who doesn't notice the new switcher.
  const [localStepMode, setLocalStepMode] = useState<PlacementStepMode>(1)
  const stepMode = stepModeProp ?? localStepMode
  const setStepMode = onStepModeChange ?? setLocalStepMode

  const pendingCounts = useMemo(
    () => derivePendingCounts(placementStack),
    [placementStack],
  )

  const armyOverrides = useMemo(() => {
    const out: Record<string, number> = {}
    for (const t of myTerritories) {
      const pending = pendingCounts[t.id] ?? 0
      if (pending > 0) out[t.id] = t.armies + pending
    }
    return out
  }, [myTerritories, pendingCounts])

  const totalPlaced = useMemo(
    () => totalStackArmies(placementStack),
    [placementStack],
  )
  const remaining = budget - totalPlaced

  const handleTerritoryPress = useCallback((territoryId: string) => {
    if (!myTerritoryIds.includes(territoryId)) return
    if (remaining <= 0) return
    // Clamp the per-tap drop to whatever's left in the budget so the
    // player never overshoots, regardless of which step mode they
    // picked (e.g. +5 with 3 remaining still lands all 3 in one tap).
    const requested = stepMode === 'max' ? remaining : stepMode
    // …and to the territory's troop cap, counting what's already pending in the
    // stack. Uncapped territories report Infinity, which is every territory on
    // every map but Capture the Flag. The server rejects an over-cap placement
    // outright, so without this the whole commit fails on one bad tap.
    const territory = state.map.territories.find((t) => t.id === territoryId)
    const pending = placementStack
      .filter((p) => p.territoryId === territoryId)
      .reduce((sum, p) => sum + p.armies, 0)
    const headroom = territory
      ? armyHeadroom({ armies: territory.armies + pending, maxArmies: territory.maxArmies })
      : Infinity
    const delta = Math.min(requested, remaining, headroom)
    if (delta <= 0) return
    setPlacementStack((prev) => [...prev, { territoryId, armies: delta }])
  }, [myTerritoryIds, remaining, stepMode, state.map.territories, placementStack])

  async function handleCommit() {
    const placementList = stackToPlacementList(placementStack)
    try {
      await reinforce.mutateAsync({ placements: placementList })
    } catch {
      // Error surfaced inline via reinforce.isError if needed; HUD has no
      // dedicated error slot, so failures are visible via the Commit button
      // staying enabled and the toast system in the parent.
    }
  }

  function handleReset() {
    setPlacementStack([])
  }

  function handleUndo() {
    setPlacementStack((prev) => prev.slice(0, -1))
  }

  const currentSlot = parseInt(state.players[state.turnIndex] ?? '0', 10)

  // Orientation-aware layout: portrait stacks map → HUD vertically;
  // landscape pins the HUD as a fixed-width sidebar on the left.
  const { isLandscape, isDesktop, actionPanelWidth } = useMapDimensions(state)
  const hud = (
    <View
      className={isLandscape ? 'p-2' : 'mx-2 mt-2'}
    >
      <PlacementHud
        remaining={remaining}
        budget={budget}
        onReset={handleReset}
        onUndo={handleUndo}
        onCommit={handleCommit}
        canReset={totalPlaced > 0 && !reinforce.isPending}
        canUndo={placementStack.length > 0 && !reinforce.isPending}
        canCommit={totalPlaced === budget && !reinforce.isPending}
        isCommitting={reinforce.isPending}
        stepMode={stepMode}
        onStepModeChange={setStepMode}
        bonusText={bonusText}
        cardChip={cardChip}
      />
    </View>
  )

  useGameMapSceneRegistration({
    key: mapSceneKey,
    onChange: onMapSceneConfigChange,
    config: {
      validTargetIds: myTerritoryIds,
      armyOverrides,
      showRegionBonuses,
      onTerritoryPress: handleTerritoryPress,
    },
  })

  const landscapeHud = (
    <View className="flex-col" style={{ width: actionPanelWidth }}>
      {actionPanelHeader}
      <ScrollView
        className={fitContent ? undefined : 'flex-1'}
        contentContainerStyle={{ padding: 8, gap: 8, flexGrow: fitContent ? 0 : 1 }}
        style={fitContent ? { flexGrow: 0, flexShrink: 1 } : undefined}
      >
        <PlacementHud
          remaining={remaining}
          budget={budget}
          onReset={handleReset}
          onUndo={handleUndo}
          onCommit={handleCommit}
          canReset={totalPlaced > 0 && !reinforce.isPending}
          canUndo={placementStack.length > 0 && !reinforce.isPending}
          canCommit={totalPlaced === budget && !reinforce.isPending}
          isCommitting={reinforce.isPending}
          stepMode={stepMode}
          onStepModeChange={setStepMode}
          bonusText={bonusText}
          cardChip={cardChip}
        />
      </ScrollView>
    </View>
  )

  if (mapSceneKey && onMapSceneConfigChange) {
    return isLandscape ? landscapeHud : hud
  }

  return (
    <View className={isLandscape ? 'flex-1 flex-row' : 'flex-1'}>
      {isLandscape ? landscapeHud : null}
      {/* Landscape-only centring; portrait keeps map's explicit height
          so the HUD below gets its 220 px reserve. See AttackUI.
          Desktop pins the map flush to the top (matches the map
          explorer); phone landscape keeps vertical centring. */}
      {isLandscape ? (
        <View className="flex-1 border-l border-olive bg-paper">
          <View className={`${isDesktop ? 'items-start' : 'flex-1 items-start justify-center'}`}>
            <MapView
              state={state}
              viewerSlot={viewerSlot}
              validTargetIds={myTerritoryIds}
              armyOverrides={armyOverrides}
              onTerritoryPress={handleTerritoryPress}
              rightOverlay={mapRightOverlay}
              regionToggleNode={regionToggleNode}
              regionToggleCorner={regionToggleCorner}
              zoomToggleNode={zoomToggleNode}
              zoomToggleCorner={zoomToggleCorner}
              showRegionBonuses={showRegionBonuses}
              hideOnMapZoomButtons={!!mapControlsBar}
              reservedBottom={mapControlsBar ? 44 : undefined}
              zoomEnabled={zoomEnabled}
              panOffset={panOffset}
              onPanChange={onPanChange}
              zoomScale={zoomScale}
              onZoomScaleChange={onZoomScaleChange}
              viewerJoinedAt={viewerJoinedAt}
            />
          </View>
          <MapControlsBarSlot state={state}>{mapControlsBar}</MapControlsBarSlot>
        </View>
      ) : (
        <MapView
          state={state}
          viewerSlot={viewerSlot}
          validTargetIds={myTerritoryIds}
          armyOverrides={armyOverrides}
          onTerritoryPress={handleTerritoryPress}
          rightOverlay={mapRightOverlay}
          regionToggleNode={regionToggleNode}
          regionToggleCorner={regionToggleCorner}
          zoomToggleNode={zoomToggleNode}
          zoomToggleCorner={zoomToggleCorner}
          showRegionBonuses={showRegionBonuses}
          hideOnMapZoomButtons={!!mapControlsBar}
          zoomEnabled={zoomEnabled}
          panOffset={panOffset}
          onPanChange={onPanChange}
          zoomScale={zoomScale}
          onZoomScaleChange={onZoomScaleChange}
          viewerJoinedAt={viewerJoinedAt}
        />
      )}
      {!isLandscape && mapControlsBar}
      {!isLandscape && hud}
    </View>
  )
}
